import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useRouterState } from '@tanstack/react-router'
import type { StaffRole } from '@johnsaks/core'
import { formatMoney } from '@johnsaks/core'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { BrandMark } from '@/components/brand'
import { Modal } from '@/components/modal'
import { api, apiMutate, type BadgeCounts, type StaffMe } from '@/lib/api'
import { filterNav, type NavEntry } from '@/lib/nav'

function Badge({ n }: { n: number }) {
  return (
    <span className="ml-auto inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-pilule bg-rouge px-1.5 text-[10px] font-medium text-white">
      {n}
    </span>
  )
}

function withBadges(entry: NavEntry, badges: BadgeCounts | undefined): NavEntry {
  if (!badges) return entry
  if (entry.to === '/stocks' && badges.lowStock > 0) {
    return { ...entry, badge: badges.lowStock }
  }
  if (entry.to === '/avis' && badges.pendingReviews > 0) {
    return { ...entry, badge: badges.pendingReviews }
  }
  if (entry.to === '/commandes' && badges.ordersToPrepare > 0) {
    return { ...entry, badge: badges.ordersToPrepare }
  }
  return entry
}

function titleForPath(chemin: string, entrees: NavEntry[]) {
  const exact = entrees.find((e) => e.to === chemin)
  if (exact) return exact.label
  if (chemin.startsWith('/produits/')) return 'Produit'
  if (chemin.startsWith('/commandes/')) return 'Commande'
  if (chemin.startsWith('/clients/')) return 'Client'
  return 'Back-office'
}

type SearchResult = {
  orders: Array<{
    reference: string
    status: string
    totalCents: number
    contactName: string
  }>
  customers: Array<{
    id: string
    email: string
    firstName: string
    lastName: string
    phone: string | null
  }>
  products: Array<{
    id: string
    sku: string
    name: string
    status: string
    priceCents: number
    category?: string
  }>
  categories?: Array<{
    id: string
    slug: string
    name: string
    tagline: string | null
    productCount: number
  }>
}

function CmdKSearch({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [q, setQ] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      setQ('')
      window.setTimeout(() => inputRef.current?.focus(), 0)
    }
  }, [open])

  const results = useQuery({
    queryKey: ['admin-search', q],
    queryFn: () => api<SearchResult>(`/admin/search?q=${encodeURIComponent(q.trim())}`),
    enabled: open && q.trim().length >= 2,
  })

  const data = results.data
  const hasHits =
    (data?.orders.length ?? 0) +
      (data?.customers.length ?? 0) +
      (data?.products.length ?? 0) +
      (data?.categories?.length ?? 0) >
    0

  return (
    <Modal open={open} title="Recherche" onClose={onClose} size="lg">
      <div className="flex flex-col gap-4">
        <input
          ref={inputRef}
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Référence, téléphone, e-mail, SKU, catégorie, variante…"
          className="champ-admin w-full px-3 py-2"
          aria-label="Recherche globale"
        />
        {q.trim().length < 2 ? (
          <p className="text-sm text-second-admin">Tapez au moins 2 caractères.</p>
        ) : results.isLoading ? (
          <p className="text-sm text-second-admin">Recherche…</p>
        ) : !hasHits ? (
          <p className="text-sm text-second-admin">Aucun résultat.</p>
        ) : (
          <div className="flex max-h-[50vh] flex-col gap-4 overflow-y-auto">
            {data?.categories?.length ? (
              <section>
                <p className="kicker mb-2 text-second-admin">Catégories</p>
                <ul className="divide-y divide-bordure-admin rounded-champ border border-bordure-admin">
                  {data.categories.map((c) => (
                    <li key={c.id}>
                      <a
                        href={`/produits?q=${encodeURIComponent(c.name)}`}
                        className="flex items-center justify-between gap-2 px-3 py-2 text-sm hover:bg-gris-fond/60"
                        onClick={onClose}
                      >
                        <span className="font-medium">{c.name}</span>
                        <span className="text-second-admin">{c.productCount} produits</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
            {data?.orders.length ? (
              <section>
                <p className="kicker mb-2 text-second-admin">Commandes</p>
                <ul className="divide-y divide-bordure-admin rounded-champ border border-bordure-admin">
                  {data.orders.map((o) => (
                    <li key={o.reference}>
                      <a
                        href={`/commandes/${o.reference}`}
                        className="flex items-center justify-between gap-2 px-3 py-2 text-sm hover:bg-gris-fond/60"
                        onClick={onClose}
                      >
                        <span>
                          <span className="font-medium">{o.reference}</span>
                          <span className="text-second-admin"> · {o.contactName}</span>
                        </span>
                        <span className="tabular-nums text-second-admin">
                          {formatMoney(o.totalCents)}
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
            {data?.customers.length ? (
              <section>
                <p className="kicker mb-2 text-second-admin">Clients</p>
                <ul className="divide-y divide-bordure-admin rounded-champ border border-bordure-admin">
                  {data.customers.map((c) => (
                    <li key={c.id}>
                      <a
                        href={`/clients/${c.id}`}
                        className="block px-3 py-2 text-sm hover:bg-gris-fond/60"
                        onClick={onClose}
                      >
                        <span className="font-medium">
                          {c.firstName} {c.lastName}
                        </span>
                        <span className="text-second-admin"> · {c.email}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
            {data?.products.length ? (
              <section>
                <p className="kicker mb-2 text-second-admin">Produits</p>
                <ul className="divide-y divide-bordure-admin rounded-champ border border-bordure-admin">
                  {data.products.map((p) => (
                    <li key={p.id}>
                      <a
                        href={`/produits/${p.id}`}
                        className="flex items-center justify-between gap-2 px-3 py-2 text-sm hover:bg-gris-fond/60"
                        onClick={onClose}
                      >
                        <span>
                          <span className="font-medium">{p.name}</span>
                          <span className="text-second-admin">
                            {' '}
                            · {p.sku}
                            {p.category ? ` · ${p.category}` : ''}
                          </span>
                        </span>
                        <span className="tabular-nums text-second-admin">
                          {formatMoney(p.priceCents)}
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
          </div>
        )}
      </div>
    </Modal>
  )
}

export function Shell({ children }: { children: ReactNode }) {
  const chemin = useRouterState({ select: (s) => s.location.pathname })
  const [cmdOpen, setCmdOpen] = useState(false)
  const qc = useQueryClient()

  const me = useQuery({
    queryKey: ['staff-me'],
    queryFn: () => api<StaffMe>('/staff/auth/me'),
    retry: false,
  })

  const badges = useQuery({
    queryKey: ['admin-badges'],
    queryFn: () => api<BadgeCounts>('/admin/badges'),
    refetchInterval: 60_000,
    enabled: me.isSuccess,
  })

  const logout = useMutation({
    mutationFn: () => apiMutate('/staff/auth/logout', 'POST'),
    onSettled: () => {
      void qc.clear()
      window.location.href = '/connexion'
    },
  })

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setCmdOpen(true)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  const role = (me.data?.role ?? null) as StaffRole | null
  const groupes = filterNav(role).map((g) => ({
    ...g,
    entrees: g.entrees.map((e) => withBadges(e, badges.data)),
  }))
  const toutes = groupes.flatMap((g) => g.entrees)
  const titre = titleForPath(chemin, toutes)

  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[232px_1fr]">
      <aside className="flex flex-col bg-encre-admin text-creme lg:min-h-dvh">
        <div className="flex items-center gap-2.5 px-5 py-4">
          <BrandMark size={28} />
          <span className="font-titre text-[17px]">John Saks</span>
          <span className="kicker ml-auto text-second-sur-noir-faible">Back-office</span>
        </div>

        <nav className="flex gap-2 overflow-x-auto px-4 pb-3 lg:hidden">
          {toutes.map((e) => (
            <Link
              key={e.to}
              to={e.to}
              className="kicker inline-flex shrink-0 items-center gap-1.5 rounded-pilule bg-encre-nav px-3 py-2 whitespace-nowrap text-second-sur-noir [&.active]:bg-rouge [&.active]:text-white"
            >
              {e.label}
              {e.badge != null && e.badge > 0 ? (
                <span className="rounded-pilule bg-white/15 px-1.5 text-[10px]">{e.badge}</span>
              ) : null}
            </Link>
          ))}
        </nav>

        <nav className="hidden flex-col gap-6 px-3 pb-8 lg:flex">
          {groupes.map((g) => (
            <div key={g.titre}>
              <p className="kicker px-2 text-second-sur-noir-faible">{g.titre}</p>
              <ul className="mt-2 flex flex-col">
                {g.entrees.map((e) => (
                  <li key={e.to}>
                    <Link
                      to={e.to}
                      className="flex min-h-9 items-center gap-2 rounded-lg px-2 text-[0.8125rem] text-second-sur-noir transition-colors hover:text-creme [&.active]:bg-encre-nav [&.active]:text-creme"
                    >
                      <span className="truncate">{e.label}</span>
                      {e.badge != null && e.badge > 0 ? <Badge n={e.badge} /> : null}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {me.data ? (
          <div className="mt-auto hidden border-t border-white/10 px-4 py-4 lg:block">
            <p className="truncate text-sm text-creme">{me.data.fullName}</p>
            <p className="truncate text-xs text-second-sur-noir-faible">{me.data.email}</p>
            <button
              type="button"
              className="kicker mt-3 text-second-sur-noir underline-offset-2 hover:text-creme hover:underline"
              onClick={() => logout.mutate()}
              disabled={logout.isPending}
            >
              Déconnexion
            </button>
          </div>
        ) : null}
      </aside>

      <div className="flex min-w-0 flex-col">
        <header className="flex flex-wrap items-center gap-3 border-b border-bordure-admin bg-white px-5 py-3">
          <div className="min-w-0">
            <p className="kicker text-second-admin">John Saks / {titre}</p>
            <h1 className="mt-0.5 truncate text-admin-titre leading-tight">{titre}</h1>
          </div>

          <div className="ml-auto flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setCmdOpen(true)}
              className="champ-admin hidden items-center gap-2 sm:flex sm:w-52"
              aria-label="Ouvrir la recherche (Cmd+K)"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                className="shrink-0 text-second-admin"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.5-3.5" />
              </svg>
              <span className="min-w-0 flex-1 text-left text-second-admin">Rechercher…</span>
              <kbd className="kicker rounded border border-bordure-admin px-1.5 py-0.5 text-[10px] text-second-admin">
                ⌘K
              </kbd>
            </button>

            {me.data ? (
              <span className="kicker hidden rounded-pilule border border-bordure-admin px-3 py-2 text-second-admin sm:inline">
                {me.data.fullName}
              </span>
            ) : null}

            <button
              type="button"
              className="kicker rounded-pilule border border-bordure-admin px-3.5 py-2 text-encre-admin lg:hidden"
              onClick={() => logout.mutate()}
              disabled={logout.isPending}
            >
              Déconnexion
            </button>
          </div>
        </header>

        <main className="min-w-0 flex-1 p-5">{children}</main>
      </div>

      <CmdKSearch open={cmdOpen} onClose={() => setCmdOpen(false)} />
    </div>
  )
}
